import {createContext, FormEvent, ReactNode, useCallback, useContext, useId, useMemo, useRef, useState} from "react";
import {Alert, Button, Form, Modal, Spinner} from "react-bootstrap";
import {ApiError} from "../api/http";
import {auth, common} from "../i18n";

// The password again, for the few changes the API asks it for (reauthentication_required): changing the email
// address, and making an account an administrator. The API doesn't ask within minutes of signing in, or of typing it
// last, so this dialog seldom shows.
type Confirm = <T>(call: (currentPassword?: string) => Promise<T>) => Promise<{ value: T } | undefined>;

interface Asking {
    error?: string;
    busy: boolean;
    resolve(password: string | null): void;
}

const PasswordConfirmationContext = createContext<Confirm | null>(null);

export function PasswordConfirmationProvider({children}: { children: ReactNode }) {
    const [asking, setAsking] = useState<Asking>();
    const [password, setPassword] = useState('');
    const titleId = useId();
    const inputId = useId();
    const askingRef = useRef<Asking | undefined>(undefined);
    askingRef.current = asking;

    // Resolves with what was typed, or null when the dialog was closed.
    const ask = useCallback((error?: string) => new Promise<string | null>(resolve => {
        setPassword('');
        setAsking({ error, busy: false, resolve });
    }), []);

    const confirm = useCallback<Confirm>(async call => {
        try {
            return { value: await call() };
        } catch (err) {
            if (!(err instanceof ApiError) || err.code !== 'reauthentication_required') {
                throw err;
            }
        }
        let error: string | undefined;
        try {
            for (;;) {
                const typed = await ask(error);
                if (typed === null) {
                    return undefined;
                }
                try {
                    return { value: await call(typed) };
                } catch (err) {
                    if (!(err instanceof ApiError) || err.code !== 'incorrect_password') {
                        throw err;
                    }
                    error = auth.confirmPassword.incorrect;
                }
            }
        } finally {
            setAsking(undefined);
        }
    }, [ask]);

    const submit = (e: FormEvent) => {
        e.preventDefault();
        const current = askingRef.current;
        if (current && password && !current.busy) {
            setAsking({ ...current, busy: true });
            current.resolve(password);
        }
    };
    const cancel = () => {
        if (askingRef.current && !askingRef.current.busy) {
            askingRef.current.resolve(null);
            setAsking(undefined);
        }
    };

    return (
        <PasswordConfirmationContext.Provider value={confirm}>
            {children}
            <Modal aria-labelledby={titleId} show={!!asking} onHide={cancel} centered>
                <Form onSubmit={submit}>
                    <Modal.Header closeButton>
                        <Modal.Title id={titleId}>{auth.confirmPassword.title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>{auth.confirmPassword.explanation}</p>
                        {asking?.error && <Alert variant="danger">{asking.error}</Alert>}
                        <Form.Group controlId={inputId}>
                            <Form.Label>{auth.confirmPassword.password}</Form.Label>
                            <Form.Control type="password" autoComplete="current-password" autoFocus required
                                          value={password} onChange={e => setPassword(e.target.value)} disabled={asking?.busy}/>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={cancel} disabled={asking?.busy}>{common.cancel}</Button>
                        <Button type="submit" disabled={asking?.busy || !password}>
                            {asking?.busy && <Spinner size="sm" className="me-2"/>}{auth.confirmPassword.submit}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PasswordConfirmationContext.Provider>
    );
}

// Makes the call, and when the API asks for the password, asks for it and makes it again with it (as
// currentPassword). { value } once it's done; undefined when the user closed the dialog instead. Other errors throw.
export function usePasswordConfirmation(): Confirm {
    const confirm = useContext(PasswordConfirmationContext);
    if (!confirm) {
        throw new Error('usePasswordConfirmation needs a PasswordConfirmationProvider');
    }
    return useMemo(() => confirm, [confirm]);
}
